import { HOW_DOES_SHOPIFY_PRICE_SYNC_WORK_ID } from "@faire/help-center-utils/articleIds";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalIntegrations from "@faire/web-api--source/routes/www/brand-portal/integrations";
import brandPortalProducts from "@faire/web-api--source/routes/www/brand-portal/my-shop/products";
import supportArticle from "@faire/web-api--source/routes/www/support/articles/articleId";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const PricingSyncWithShopifyIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you’re experiencing issues with the <BoldFormatting>pricing sync</BoldFormatting> feature of your Shopify integration, follow these steps to troubleshoot:"
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Introduction to troubleshooting pricing sync with Shopify",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="From the <BoldFormatting>Settings</BoldFormatting> page, click <LinkFormatting>Integrations</LinkFormatting> to go to the <BoldFormatting>Faire: Sell Wholesale</BoldFormatting> sales channel in <BoldFormatting>Shopify</BoldFormatting>."
              values={{
                LinkFormatting: IntegrationsLinkFormatting,
                BoldFormatting,
              }}
              description={{
                text: "First step to troubleshoot pricing sync",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Once on Shopify and in the Faire sales channel, go to <BoldFormatting>Preferences</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Second step to troubleshoot pricing sync",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Scroll to the <BoldFormatting>price sync</BoldFormatting> section and ensure the setting is enabled. If it’s off, select the <BoldFormatting>Turn On</BoldFormatting> button."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to troubleshoot pricing sync",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Verify that your products are linked correctly by going to the <BoldFormatting>Product Linking</BoldFormatting> tab on the left side of the Shopify dashboard under the <BoldFormatting>Faire: Sell Wholesale</BoldFormatting> sales channel. If there are any linking issues, a banner at the top of the page will indicate how many products are affected."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to troubleshoot pricing sync",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="On Faire, go to your <LinkFormatting>Products</LinkFormatting> page and check that your prices and currency on Faire match what’s in Shopify."
              values={{
                LinkFormatting: ProductsLinkFormatting,
              }}
              description={{
                text: "Fifth step to troubleshoot pricing sync",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="If these steps don’t resolve your pricing sync issue, contact our support team for more help."
          description={{
            text: "Conclusion for troubleshooting pricing sync",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="For more detailed information on pricing sync, visit <LinkFormatting>How does Shopify price sync work</LinkFormatting>."
          values={{
            LinkFormatting: ArticleLinkFormatting,
          }}
          description={{
            text: "Link to the article on how Shopify price sync works",
          }}
        />
      </Typography>
    </Column>
  );
};

const IntegrationsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalIntegrations()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const ArticleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={supportArticle(HOW_DOES_SHOPIFY_PRICE_SYNC_WORK_ID)}
    target="_blank"
  >
    {chunks}
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
